import { PackageIcon } from "@phosphor-icons/react/Package";
import { Page } from "@freightsimple/ui-core/PageLayout/Page";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Tabs } from "@freightsimple/ui-core/PageLayout/Tabs";
import { carriersApi } from "@/App/Auth/carrierApi";
import { describeShipmentState } from "@/App/Views/carrierFormatting";
import { ShipmentBanners } from "@/App/Shipment/ShipmentBanners";
import { ShipmentKeyInfoTab } from "@/App/Shipment/ShipmentKeyInfoTab";
import { ShipmentTrackingTab } from "@/App/Shipment/ShipmentTrackingTab";
import { ShipmentDocumentsTab } from "@/App/Shipment/ShipmentDocumentsTab";
import { ShipmentInvoiceTab } from "@/App/Shipment/ShipmentInvoiceTab";
import { CarrierLoadFailed } from "@/App/CarrierLoadFailed";
import { useCarrierFetch } from "@/App/useCarrierFetch";
import { navigateTo } from "@/App/navigateTo";
import { shipmentHref } from "@/App/carrierRoutes";
import { CarrierPageLogo } from "@/App/CarrierPageLogo";

/**
 * One booked shipment — the portal's work surface.
 *
 * Everything else is an index into this page: the operational issues todo list,
 * the invoice audit list and the shipments list are three questions that all
 * resolve here. Anything a carrier needs to do about a shipment happens in the
 * banners above the tabs; the tabs are what the shipment *is*.
 *
 * Tab selection lives in `?tab=`, handled by ui-core `Tabs`, so a link can point
 * at a specific tab.
 */
export function ShipmentPage({ shipmentId }: { shipmentId: string }) {
  const { state, refreshing, refresh } = useCarrierFetch(
    () => carriersApi().getCarrierShipment({ shipmentId }),
    shipmentId,
  );
  const shipment = state.status === "loaded" ? state.data : undefined;

  // Fetched alongside the shipment rather than on tab open: the audit banner
  // needs the numbers to say anything useful, and it sits above the tabs.
  const invoiceFetch = useCarrierFetch(
    () => carriersApi().getCarrierShipmentInvoice({ shipmentId }),
    shipmentId,
  );
  const invoice =
    invoiceFetch.state.status === "loaded"
      ? invoiceFetch.state.data
      : undefined;

  const trackingFetch = useCarrierFetch(
    () => carriersApi().getCarrierShipmentTracking({ shipmentId }),
    shipmentId,
  );
  const trackingLines =
    trackingFetch.state.status === "loaded"
      ? trackingFetch.state.data.lines
      : undefined;

  const documentsFetch = useCarrierFetch(
    () => carriersApi().getCarrierShipmentDocuments({ shipmentId }),
    shipmentId,
  );
  const documents =
    documentsFetch.state.status === "loaded"
      ? documentsFetch.state.data.documents
      : undefined;

  // "See why" on the audit banner is navigation, not a submission: the evidence
  // lives in the Invoice tab and audits are settled by email.
  function openInvoiceTab() {
    navigateTo(shipmentHref(shipmentId, "invoice"));
  }

  return (
    <Page
      leadingVisual={<CarrierPageLogo />}
      icon={PackageIcon}
      title={shipment?.proNumber ?? "Shipment"}
      description={shipment ? describeShipmentState(shipment.state) : undefined}
      refreshing={refreshing}
    >
      {state.status === "error" && (
        <CarrierLoadFailed what="this shipment" onRetry={refresh} />
      )}
      {shipment && (
        <Stack gap={4}>
          <ShipmentBanners
            shipment={shipment}
            invoice={invoice}
            onResponded={refresh}
            onSeeWhy={openInvoiceTab}
          />
          <Tabs>
            <ShipmentKeyInfoTab shipment={shipment} />
            <ShipmentTrackingTab lines={trackingLines} />
            <ShipmentDocumentsTab
              shipmentId={shipmentId}
              documents={documents}
            />
            <ShipmentInvoiceTab invoice={invoice} />
          </Tabs>
        </Stack>
      )}
    </Page>
  );
}
